/**
 * MainLayout — Bloomcode CMS layout using @bloomcode/app-shell.
 */

import { useCallback, useMemo } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useCookieSession } from '@bloomcode/auth';
import { useSessionManager } from '@bloomcode/shared-modules';
import { getAuthPortalLoginUrl, getAuthPortalLogoutUrl } from '@/config/env';
import { LoadingSpinner } from '@bloomcode/ui';
import { AppShell } from '@bloomcode/app-shell';
import type { NavItem, CommandCenterConfig, ExternalApp } from '@bloomcode/app-shell';
import {
  CrmIcon,
  CmsIcon,
  CommerceIcon,
  BillingIcon,
  SupportIcon,
  HrIcon,
  ProjectsIcon,
  ChatbotIcon,
  LedgerIcon,
} from '@bloomcode/icons';
import { useTheme } from '@bloomcode/theme';
import { useUpdateUserSettingsMutation, useGetActiveTenantModulesQuery } from '@bloomcode/graphql';
import {
  Activity,
  ExternalLink,
  FileText,
  Key,
  LayoutDashboard,
  MessageSquare,
  Search,
  Settings,
} from '@bloomcode/icons';
import { getAppUrl } from '@bloomcode/config';

interface MainLayoutProps {
  requireAuth?: boolean;
}

const navItems: NavItem[] = [
  { path: '/content', label: 'Content', icon: FileText },
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/search', label: 'Search', icon: Search },
  { path: '/preferences', label: 'Preferences', icon: Settings },
];

const accountUrl = getAppUrl('portal-account');
const supportUrl = getAppUrl('portal-support');
const docsUrl = getAppUrl('portal-api-docs');
const accountPortalUrl = getAppUrl('portal-account');

const commandCenterConfig: CommandCenterConfig = {
  quickSettings: true,
  help: [
    { label: 'Documentation', icon: FileText, url: docsUrl },
    {
      label: 'API Reference',
      icon: ExternalLink,
      url: `${docsUrl}/api`,
    },
    { label: 'Contact Support', icon: MessageSquare, url: `${supportUrl}/tickets/new` },
  ],
  developerTools: [
    {
      label: 'API Keys',
      icon: Key,
      description: 'Manage your API keys',
      url: `${accountUrl}/api-keys`,
    },
    {
      label: 'Webhooks',
      icon: ExternalLink,
      description: 'Configure webhook endpoints',
      url: `${accountUrl}/webhooks`,
    },
    {
      label: 'API Documentation',
      icon: FileText,
      description: 'REST & GraphQL API docs',
      url: `${docsUrl}/api`,
    },
  ],
  ads: [
    {
      name: 'Bloomcode AI',
      description: 'Supercharge your workflow with AI-powered insights and automation.',
      ctaLabel: 'Learn More',
      ctaUrl: 'https://bloomcodetechnologies.com/ai',
      accentColor: '#6366f1',
    },
  ],
};

export default function MainLayout({ requireAuth = false }: MainLayoutProps) {
  const { isAuthenticated, isLoading, session, logout } = useCookieSession();
  const location = useLocation();
  const { theme, setTheme } = useTheme();
  const [updateUserSettings] = useUpdateUserSettingsMutation();

  const { data: modulesData } = useGetActiveTenantModulesQuery({
    variables: { tenantId: session?.tenantId || '' },
    skip: !isAuthenticated || !session?.tenantId || session?.isPlatformAdmin === true,
    fetchPolicy: 'cache-first',
  });

  const externalApps: ExternalApp[] = useMemo(() => {
    const scopeLevel = session?.scopeLevel;
    const isPlatform = session?.isPlatformAdmin === true || scopeLevel === 'PLATFORM';
    const isApp = session?.isAppAdmin === true || scopeLevel === 'APP';

    const workspaceApps: ExternalApp[] = [
      { name: 'CRM', icon: CrmIcon, url: getAppUrl('app-crm'), category: 'workspace' },
      { name: 'CMS', icon: CmsIcon, url: getAppUrl('app-cms'), category: 'workspace' },
      {
        name: 'Commerce',
        icon: CommerceIcon,
        url: getAppUrl('app-commerce'),
        category: 'workspace',
      },
      {
        name: 'Projects',
        icon: ProjectsIcon,
        url: getAppUrl('app-projects'),
        category: 'workspace',
      },
      { name: 'Chatbot', icon: ChatbotIcon, url: getAppUrl('app-chatbot'), category: 'workspace' },
      { name: 'Billing', icon: BillingIcon, url: getAppUrl('app-billing'), category: 'workspace' },
      {
        name: 'Support',
        icon: SupportIcon,
        url: getAppUrl('portal-support'),
        category: 'workspace',
      },
      { name: 'HR', icon: HrIcon, url: getAppUrl('app-hr'), category: 'workspace' },
      { name: 'Books', icon: LedgerIcon, url: getAppUrl('portal-books'), category: 'workspace' },
    ];

    const adminApps: ExternalApp[] = [
      {
        name: 'Platform Admin',
        icon: LayoutDashboard,
        url: getAppUrl('admin-platform'),
        category: 'admin',
      },
      { name: 'Tenant Admin', icon: Settings, url: getAppUrl('admin-tenant'), category: 'admin' },
    ];

    const accountApps: ExternalApp[] = [
      { name: 'Account', icon: Settings, url: accountUrl, category: 'account' },
      { name: 'Billing', icon: BillingIcon, url: getAppUrl('portal-billing'), category: 'account' },
      {
        name: 'Dashboard',
        icon: LayoutDashboard,
        url: getAppUrl('portal-dashboard'),
        category: 'account',
      },
    ];

    if (isPlatform) return [...adminApps, ...workspaceApps, ...accountApps];
    if (isApp) return [...workspaceApps, ...accountApps];

    const modules = modulesData?.getActiveTenantModules?.data ?? [];
    const activeCodes = new Set(modules.map((m) => m.module?.code).filter(Boolean));
    const moduleToAppName: Record<string, string> = {
      CRM: 'CRM',
      CMS: 'CMS',
      COMMERCE: 'Commerce',
      PROJECTS: 'Projects',
      CHATBOT: 'Chatbot',
      BILLING: 'Billing',
      SUPPORT: 'Support',
      HR: 'HR',
      LEDGER: 'Books',
    };
    const tenantWorkspaceApps = workspaceApps.filter((app) => {
      const code = Object.entries(moduleToAppName).find(([, name]) => name === app.name)?.[0];
      return code && activeCodes.has(code);
    });
    return [...tenantWorkspaceApps, ...accountApps];
  }, [
    session?.scopeLevel,
    session?.isPlatformAdmin,
    session?.isAppAdmin,
    modulesData,
    session?.tenantId,
  ]);

  useSessionManager({
    enabled: requireAuth && isAuthenticated,
    onSessionExpired: () => {
      window.location.href =
        getAuthPortalLogoutUrl(window.location.origin) + '?reason=session_expired';
    },
  });

  const handleLogout = useCallback(async () => {
    try {
      await logout();
    } catch (err) {
      console.error('Logout failed:', err);
    }
    window.location.href = getAuthPortalLogoutUrl(window.location.origin);
  }, [logout]);

  const handleSignIn = useCallback(() => {
    window.location.href = getAuthPortalLoginUrl(window.location.href);
  }, []);

  const handleProfileClick = useCallback(() => {
    window.location.href = `${accountPortalUrl}/profile`;
  }, []);

  if (isLoading) return <LoadingSpinner className="h-screen" />;

  if (requireAuth && !isAuthenticated) {
    const returnUrl = window.location.origin + location.pathname + location.search;
    if (typeof window !== 'undefined') window.location.href = getAuthPortalLoginUrl(returnUrl);
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  const user = session ? { email: session.email || '' } : undefined;

  return (
    <>
      <Helmet>
        <title>Bloomcode CMS Portal</title>
        <meta name="description" content="Bloomcode CMS Portal" />
      </Helmet>

      <AppShell
        app={{ name: 'Bloomcode CMS', icon: CmsIcon }}
        navigation={navItems}
        user={user}
        isAuthenticated={isAuthenticated}
        onLogout={handleLogout}
        onProfileClick={handleProfileClick}
        onSignIn={handleSignIn}
        commandCenter={{
          ...commandCenterConfig,
          externalApps,
          quickSettings: {
            enabled: true,
            currentTheme: theme as 'light' | 'dark' | 'system',
            onThemeChange: async (newTheme) => {
              setTheme(newTheme);
              try {
                await updateUserSettings({
                  variables: { input: { themePreference: newTheme.toUpperCase() } },
                });
              } catch {
                /* non-blocking */
              }
            },
          },
        }}
        quickStatus={[
          { label: 'Open Tickets', value: 0, icon: MessageSquare, variant: 'default' },
          { label: 'Account', value: 'Active', icon: Activity, variant: 'success' },
        ]}
        quickActions={[
          {
            label: 'New Support Ticket',
            icon: MessageSquare,
            onClick: () => window.open(`${supportUrl}/tickets/new`, '_blank'),
          },
          { label: 'View Profile', icon: Settings, onClick: handleProfileClick },
        ]}
        footerAppName="Bloomcode CMS Portal"
      >
        <Outlet />
      </AppShell>
    </>
  );
}
